// src/components/game/veridian/GrammarData.js

// --- 1. VOCABULARY BANKS (Professional & Sci-Fi Context) ---

const SUBJECTS = [
    // 3rd Person Singular (Requires 'S')
    "The Chief Engineer", "The Reactor Core", "The Security System", "The Drone Pilot", 
    "Dr. Vance", "The Algorithm", "The Supply Chain", "The Encryption Key", 
    "The Backup Generator", "The AI", "The Technician", "The Output Log",
    // Plural / 1st / 2nd (No 'S')
    "I", "You", "We", "They", "The Engineers", "The Sensors", "The Data Streams", 
    "The Protocols", "The Sectors", "The Drones"
];

const VERBS = [
    // Technical Actions
    "monitor", "analyze", "calibrate", "restrict", "authorize", "transmit",
    "upload", "download", "encrypt", "decrypt", "stabilize", "breach",
    "navigate", "calculate", "terminate", "initiate", "override", "scan",
    "detect", "avoid", "repair", "construct", "extract", "refine"
];

const OBJECTS = [
    "the anomaly", "the data stream", "the cargo shipment", "the bio-readings",
    "the security protocol", "the access codes", "the thermal variance",
    "the output logs", "the frequency", "the distress signal", "the blueprints",
    "the raw materials", "the energy signature", "the quantum state"
];

const PLACES = [
    "in Sector 7", "at the Control Node", "behind the firewall", "on the bridge",
    "under the reactor", "in the vacuum chamber", "at the airlock",
    "in the orbital station", "across the network", "within the simulation"
];

const TIMES = {
    habit: ["every cycle", "usually", "often", "always", "daily", "frequently", "automatically"],
    now: ["at this moment", "currently", "right now", "presently", "in this phase"],
    past: ["yesterday", "during the collapse", "last cycle", "an hour ago", "previously"],
    future: ["tomorrow", "next rotation", "soon", "in the future", "eventually", "shortly"]
};

// --- 2. HELPER FUNCTIONS ---
const getRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];

// --- 3. PROCEDURAL GENERATORS (The Logic) ---

// GENERATOR: S-TRAP (Present Simple Agreement)
export const generateSTrap = () => {
    const s = getRandom(SUBJECTS);
    const v = getRandom(VERBS);
    const o = getRandom(OBJECTS);
    const t = getRandom(TIMES.habit);
    
    // Logic: He/She/It/Titles take 'S'. I/You/We/They/Plurals do not.
    const isPluralOrFirst = ["I", "You", "We", "They"].includes(s) || s.endsWith("s");
    const isThirdPerson = !isPluralOrFirst;

    const correct = isThirdPerson ? v + "s" : v;
    const wrong = isThirdPerson ? v : v + "s";

    return {
        text: `STATUS REPORT: ${s} ______ ${o} ${t}.`,
        options: [
            { id: "c", label: correct, correct: true, msg: "Subject-Verb Agreement Verified." },
            { id: "w", label: wrong, correct: false, msg: "Error: Check Subject Number." }
        ].sort(() => Math.random() - 0.5)
    };
};

// GENERATOR: TENSE ALIGNMENT (Time Markers)
export const generateTense = () => {
    const s = getRandom(SUBJECTS);
    const v = getRandom(VERBS);
    const o = getRandom(OBJECTS);
    
    const mode = getRandom(['past', 'future', 'now']);
    let time, correct, wrong, ctx;

    if (mode === 'past') {
        time = getRandom(TIMES.past);
        correct = v.endsWith('e') ? v + 'd' : v + 'ed';
        wrong = v + "s";
        ctx = "ARCHIVE LOG:";
    } else if (mode === 'future') {
        time = getRandom(TIMES.future);
        correct = "will " + v;
        wrong = v + "ed";
        ctx = "PREDICTION MODEL:";
    } else {
        time = getRandom(TIMES.now);
        correct = "is " + (v.endsWith('e') ? v.slice(0,-1) : v) + "ing";
        wrong = "will " + v;
        ctx = "LIVE FEED:";
    }

    return {
        text: `${ctx} ${s} ______ ${o} ${time}.`,
        options: [
            { id: "c", label: correct, correct: true, msg: "Temporal Alignment Valid." },
            { id: "w", label: wrong, correct: false, msg: "Temporal Mismatch." }
        ].sort(() => Math.random() - 0.5)
    };
};

// GENERATOR: CAUSATIVE (Have/Get Done)
export const generateCausative = () => {
    const s = getRandom(["The Director", "We", "The System", "Security"]);
    const o = getRandom(OBJECTS);
    const v = getRandom(VERBS);
    const pp = v.endsWith('e') ? v + 'd' : v + 'ed'; // Past Participle approximation

    return {
        text: `COMMAND: ${s} needs to have ${o} ______ immediately.`,
        options: [
            { id: "c", label: pp, correct: true, msg: "Passive Action Confirmed." },
            { id: "w", label: v + "ing", correct: false, msg: "Gerund Invalid." }
        ].sort(() => Math.random() - 0.5)
    };
};

// GENERATOR: IDENTITY (To Be)
export const generateIdentity = () => {
    const s = getRandom(["I", "You", "He", "She", "It", "We", "They", "The Signal", "The Drones"]);
    let ans = "is";
    if (s === "I") ans = "am";
    else if (["You", "We", "They", "The Drones"].includes(s)) ans = "are";

    return {
        text: `LOGIN PROTOCOL: ${s} ______ authorized for Sector 9.`,
        options: [
            { id: "am", label: "am", correct: ans === "am" },
            { id: "is", label: "is", correct: ans === "is" },
            { id: "are", label: "are", correct: ans === "are" }
        ]
    };
};

// --- 4. STATIC POOLS (Specific Topics) ---

export const PREPOSITION_POOL = [
    { text: "The server is located ___ the secure room.", options: [{id:'in', label:'in', correct:true}, {id:'on', label:'on', correct:false}] },
    { text: "Do not leave data ___ the desk.", options: [{id:'on', label:'on', correct:true}, {id:'in', label:'in', correct:false}] },
    { text: "Meet me ___ the main entrance.", options: [{id:'at', label:'at', correct:true}, {id:'in', label:'in', correct:false}] },
    { text: "The drone is hovering ___ the city.", options: [{id:'above', label:'above', correct:true}, {id:'on', label:'on', correct:false}] }
];

export const SOME_ANY_POOL = [
    { text: "INVENTORY: We have ___ backup generators online.", options: [{id:'s', label:'some', correct:true}, {id:'a', label:'any', correct:false}] },
    { text: "QUERY: Do we have ___ spare parts?", options: [{id:'a', label:'any', correct:true}, {id:'s', label:'some', correct:false}] },
    { text: "WARNING: There isn't ___ signal here.", options: [{id:'a', label:'any', correct:true}, {id:'s', label:'some', correct:false}] },
    { text: "REQUEST: I need ___ assistance immediately.", options: [{id:'s', label:'some', correct:true}, {id:'a', label:'any', correct:false}] }
];

export const PHRASAL_POOL = [
    { text: "ALERT: The engine blew ___ during takeoff.", options: [{id:'up', label:'up', correct:true}, {id:'out', label:'out', correct:false}] },
    { text: "ACTION: Please turn ___ the emergency lights.", options: [{id:'on', label:'on', correct:true}, {id:'in', label:'in', correct:false}] },
    { text: "STATUS: We have run ___ of fuel.", options: [{id:'out', label:'out', correct:true}, {id:'off', label:'off', correct:false}] }
];

export const REPORTER_POOL = [
    { text: "DIRECT: 'I am tracking the target.'\nLOG: He said he ___ tracking the target.", options: [{id:'was', label:'was', correct:true}, {id:'is', label:'is', correct:false}] },
    { text: "DIRECT: 'We will secure the perimeter.'\nLOG: They said they ___ secure it.", options: [{id:'would', label:'would', correct:true}, {id:'will', label:'will', correct:false}] },
    { text: "DIRECT: 'I can hack the gate.'\nLOG: She said she ___ hack it.", options: [{id:'could', label:'could', correct:true}, {id:'can', label:'can', correct:false}] }
];

export const FUTURE_POOL = [
    { text: "SIMULATION: If the temperature rises, the core ___ melt.", options: [{id:'will', label:'will', correct:true}, {id:'would', label:'would', correct:false}] },
    { text: "HYPOTHESIS: If I knew the password, I ___ enter.", options: [{id:'would', label:'would', correct:true}, {id:'will', label:'will', correct:false}] },
    { text: "WARNING: Unless we act now, we ___ fail.", options: [{id:'will', label:'will', correct:true}, {id:'would', label:'would', correct:false}] }
];